import { useCallback, useState } from 'react';

import { ImageUploadClient } from '@spotify/confidence-backstage-image-upload';
import { useAlert } from '@spotify/confidence-core-react';

import { validateImageFile } from '../ImageUploadPlugin';

interface UseFileUploadOptions {
  imageUploadClient: ImageUploadClient;
  onUploadComplete: (data: { src: string; alt: string }) => void;
}

export const useFileUpload = ({
  imageUploadClient,
  onUploadComplete,
}: UseFileUploadOptions) => {
  const [isUploading, setIsUploading] = useState(false);
  const alert = useAlert();

  const handleFileSelect = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!file.type.startsWith('image/')) {
        alert.post({
          message: 'Please select an image file',
          severity: 'error',
        });
        return;
      }

      if (
        !validateImageFile(file, error =>
          alert.post({ message: error.message, severity: 'error' }),
        )
      ) {
        return;
      }

      setIsUploading(true);

      try {
        const imageUri = await imageUploadClient.upload(file);
        onUploadComplete({
          src: imageUri,
          alt: file.name,
        });
      } catch (error) {
        const message =
          error instanceof Error ? error.message : 'Failed to upload image';
        alert.post({
          message: `Failed to upload image: ${message}`,
          severity: 'error',
        });
      } finally {
        setIsUploading(false);
      }
    },
    [onUploadComplete, alert, imageUploadClient],
  );

  return {
    isUploading,
    handleFileSelect,
  };
};
